import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { AccessTokenService } from 'src/app/Services/access-token.service';
import { AlertService } from 'src/app/Services/alert.service';
import { SettingsService } from 'src/app/Services/settings.service';
import { UserDataProviderService } from 'src/app/Services/user-data-provider.service';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LocalStorageService } from 'src/app/Services/local-storage.service';
import { MaterialModule } from 'src/app/Shared/MaterialModule/material/material.module';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalModule, MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { Subject, } from 'rxjs';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { TermsConditionsComponent } from 'src/app/PopUps/terms-conditions/terms-conditions.component';
import { PrivacyPolicyComponent } from 'src/app/PopUps/privacy-policy/privacy-policy.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PingSsoService, SsoProviderDescriptor } from 'src/app/Services/ping-sso.service';
import { AnalyticsService } from 'src/app/Services/analytics.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, MaterialModule, FormsModule, ReactiveFormsModule,
    MsalModule, RouterOutlet, RouterLink, TranslateModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  /** Per-host localStorage cache key for the providers descriptor list. */
  private static readonly SSO_PROVIDERS_CACHE_KEY_PREFIX = 'ssoProvidersCache_';
  /** Cache TTL on the SPA side; backend cache + KV reload still keep it fresh. */
  private static readonly SSO_PROVIDERS_CACHE_TTL_MS = 60_000;
  /** Safety-net cap on how long the skeleton can render before falling back to legacy buttons. */
  private static readonly SSO_PROVIDERS_LOADING_TIMEOUT_MS = 8000;
  /** localStorage key for the deployment's last reported passwordLogin; also read by the header. */
  private static readonly PASSWORD_LOGIN_KEY = 'passwordLoginEnabled';

  loginFormGroup: FormGroup;
  loginCurrentState = '';
  submitted = false;
  errorMessage = '';
  errorAlert = false;

  loginSsoErrorMessage = '';
  onSSOEnabled = false;
  SsoSettings: any;
  isSSOEnabled = false;
  OktaSsoSettings: any;
  isOktaSSOEnabled = false;

  pingProvider: SsoProviderDescriptor | null = null;
  oktaProvider: SsoProviderDescriptor | null = null;
  microsoftProvider: SsoProviderDescriptor | null = null;
  /**
   * When a tenant-specific SSO provider (Ping) is enabled, hide the other
   * external IdP buttons (Okta, Microsoft) — but keep the username/password
   * form available so Spendkey admins can sign into the customer tenant for
   * support and troubleshooting.
   */
  hideOtherSsoButtons = false;

  /**
   * Okta / Microsoft button visibility. Both show by default; when any of the
   * tenant's configured providers is marked `exclusive`, only the configured
   * providers' buttons show (e.g. a Microsoft-only tenant loses the Okta one).
   */
  showOktaButton = true;
  showMicrosoftButton = true;

  /**
   * Email/password form visibility. Stays true unless the backend explicitly
   * reports `passwordLogin: false` (an SSO-only deployment); a missing field
   * (older backend) or a failed call keeps the form.
   */
  passwordLoginEnabled = true;

  /**
   * Gates the email/password form. False until the deployment's passwordLogin
   * is known, either from the value kept from an earlier visit or from the
   * providers call (or its failure/timeout), so an SSO-only deployment never
   * shows the form for a beat before hiding it.
   */
  passwordLoginResolved = false;

  /**
   * Gates the SSO-button row in the template. True until either the
   * localStorage cache hydrates or the backend providers call resolves
   * (success or error). Prevents the flash where Okta and Microsoft
   * render first and disappear once Ping is discovered.
   */
  ssoProvidersLoading = true;

  /** Pending fallback timer so we can clear it in ngOnDestroy. */
  private ssoProvidersFallbackTimer: ReturnType<typeof setTimeout> | null = null;

  loginLogo: any = 'assets/new-icons/Spendkey_logo_dark.svg'
  spendkeyFav = false;
  clientName: any = 'Spendkey';
  spendkeyLoadLogo = false;

  isIframe = false;
  private readonly _destroying$ = new Subject<void>();
  fieldTextType = false;

  constructor(
    private titleService: Title,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private _userDataProviderService: UserDataProviderService,
    private routerService: Router,
    public accessTokenService: AccessTokenService,
    private route: ActivatedRoute,
    private SettingsService: SettingsService,
    public dialog: MatDialog,
    private localStorage: LocalStorageService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private translate: TranslateService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private pingSso: PingSsoService,
    private analytics: AnalyticsService,
  ) {
    this.loginFormGroup = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]

    });
    this.titleService.setTitle('Login - Spendkey');
  }

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener;

    // if (this.localStorage.getItem("SSOLoginInProgress")) {
    //   this.accessTokenService.showLoginUI = false;
    // } else {
    //   this.accessTokenService.showLoginUI = true;
    // }

    if (this.accessTokenService.isTokenAvailable() && !this.accessTokenService.isTokenExpired()) {
      this.routerService.navigateByUrl('/dashboard');
      this.accessTokenService.showLoginUI = false;
    } else {
      // The login page wipes localStorage on every visit to scrub any
      // session residue. Snapshot the SSO providers cache around the
      // wipe so warm visits can still render the correct button set
      // instantly — without this, every reload would re-show Okta+
      // Microsoft for a beat before Ping arrives from the backend.
      // Guarded by isPlatformBrowser because cacheKeyForHost reads
      // window.location and would NRE during Angular Universal SSR.
      const ssoCacheKey = this.cacheKeyForHost();
      const ssoCacheValue = ssoCacheKey ? this.localStorage.getItem(ssoCacheKey) : null;
      const passwordLoginValue = this.localStorage.getItem(LoginComponent.PASSWORD_LOGIN_KEY);
      this.localStorage.clear();
      if (ssoCacheKey && ssoCacheValue !== null) {
        this.localStorage.setItem(ssoCacheKey, ssoCacheValue);
      }
      if (passwordLoginValue != null) {
        this.localStorage.setItem(LoginComponent.PASSWORD_LOGIN_KEY, passwordLoginValue);
      }
      this._userDataProviderService.isShowSidebar.emit(false);
      this.routerService.navigate(['login']);
      this.accessTokenService.showLoginUI = true;
    }


    this.localStorage.setItem('URL', location.origin);

    this.getClientDetails();

    this.getSSOEnabledSetting();
    this.getOktaSSOEnabledSetting();
    this.loadTenantSsoProviders();
    // Check if we are running in the browser
    if (isPlatformBrowser(this.platformId)) {
      // Make sure window is available (for safety)
      if (typeof window !== 'undefined') {
        if (window.location.host === 'techm.spendkey.app') {
          this.clientName = 'Tech Mahindra';
          this.spendkeyFav = true; // dynamic CSS applied
        }

        if (window.location.host === 'timetoact.spendkey.app') {
          this.clientName = 'Time to Act Group';
          this.spendkeyFav = true; // dynamic CSS applied
        }
      }
    } else {
    }

    if (this.accessTokenService.isTokenAvailable()) {
      this.routerService.navigateByUrl('/dashboard');
    }

    // Check for SSO error and show message
    this.route.queryParams.subscribe((params) => {
      if (params['error'] === 'UNAUTHORIZED_SSO_LOGIN') {
        this.loginSsoErrorMessage = this.translate.instant('Your account is not registered with Spendkey !')
        this.accessTokenService.showLoginUI = true;
      }
    });
  }

  getOktaSSOEnabledSetting() {
    this.SettingsService.isOktaSSOEnabled().subscribe((response: any) => {
      if (response.status == 'success') {
        this.OktaSsoSettings = response.data[0].OktaSSOEnabled;
        this.isOktaSSOEnabled = this.OktaSsoSettings == 'True';
        this.localStorage.setItem('isOktaSSOEnabled', this.isOktaSSOEnabled ? 'true' : 'false');
      }
    });
  }

  getSSOEnabledSetting() {
    this.SettingsService.isSSOEnabled().subscribe((response: any) => {
      this.errorMessage = '';
      if (response.status == 'success') {
        this.SsoSettings = response.data[0].ssoEnabled;
        if (this.SsoSettings == 'False') {
          this.isSSOEnabled = false;
        }
        if (this.SsoSettings == 'True') {
          this.isSSOEnabled = true;
        }
      }
    });
  }

  onPassEmail() {
    this.submitted = true;

    if (this.loginCurrentState == '') {
      if (this.loginFormGroup.valid) {
        this.loginCurrentState = 'EmailSubmitted';
        this.loginFormGroup.controls['password'].enable();
        this.loginFormGroup.controls['password'].setValidators(Validators.required);
      }
    }
  }

  onSingleSignOn() {
    if (this.isIframe) return;

    // if (this.isSSOEnabled === false) {
    //   this.alertService.openAlert('Please login using username and password!', 'error');
    //   return;
    // }
    // // REPLACE WITH CONCRETE LOGIC - 
    this.localStorage.setItem('SSOLoginInProgress', 'true');
    this.accessTokenService.showLoginUI = false;

    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }

  }

  private loadTenantSsoProviders() {
    // 1. Hydrate from localStorage if we have a recent cache for this
    //    host. On warm visits this lets the row render with the correct
    //    buttons immediately while the backend call refreshes in the
    //    background, so the user never sees Okta+Microsoft flash before
    //    Ping appears.
    const cached = this.readCachedSsoProviders();
    if (cached) {
      this.applySsoProviders(cached);
      this.ssoProvidersLoading = false;
    }
    const knownPasswordLogin = this.localStorage.getItem(LoginComponent.PASSWORD_LOGIN_KEY);
    if (knownPasswordLogin === 'true' || knownPasswordLogin === 'false') {
      this.passwordLoginEnabled = knownPasswordLogin === 'true';
      this.passwordLoginResolved = true;
    }

    // 2. Safety net: if the backend hangs (Angular's HttpClient has no
    //    default request timeout) the skeleton would render forever and
    //    the user couldn't even reach Okta/Microsoft. Always release the
    //    gate after a few seconds so legacy buttons surface as a fallback.
    this.ssoProvidersFallbackTimer = setTimeout(() => {
      this.ssoProvidersLoading = false;
      this.passwordLoginResolved = true;
      this.ssoProvidersFallbackTimer = null;
    }, LoginComponent.SSO_PROVIDERS_LOADING_TIMEOUT_MS);

    // 3. Always hit the backend so a stale cache eventually converges.
    this.SettingsService.getSsoProviders().subscribe({
      next: (response: any) => {
        this.clearSsoFallbackTimer();
        if (response?.status !== 'success' || !Array.isArray(response.data)) {
          this.ssoProvidersLoading = false;
          this.passwordLoginResolved = true;
          return;
        }
        this.applySsoProviders(response.data);
        this.writeCachedSsoProviders(response.data);
        this.passwordLoginEnabled = response.passwordLogin !== false;
        // Read by the header to hide Change Password on SSO-only deployments.
        this.localStorage.setItem(LoginComponent.PASSWORD_LOGIN_KEY, this.passwordLoginEnabled ? 'true' : 'false');
        this.passwordLoginResolved = true;
        this.ssoProvidersLoading = false;
      },
      error: () => {
        // Failure mode: surface the legacy buttons rather than leave the
        // user staring at skeletons forever. Cached state, if any, stays.
        this.clearSsoFallbackTimer();
        this.ssoProvidersLoading = false;
        this.passwordLoginResolved = true;
      },
    });
  }

  private applySsoProviders(descriptors: SsoProviderDescriptor[]) {
    const byType = (name: string) => descriptors.find(
      (p: SsoProviderDescriptor) => p.type?.toLowerCase() === name,
    ) ?? null;
    const ping = byType('ping');
    if (ping) {
      this.pingProvider = ping;
      // Ping is the IdP for this tenant — suppress the other external
      // provider buttons so the SSO flow is unambiguous. The local
      // password form stays visible (Spendkey admin break-glass).
      this.hideOtherSsoButtons = true;
    } else {
      this.pingProvider = null;
      this.hideOtherSsoButtons = false;
    }
    // Okta/Microsoft descriptors carry a per-tenant logoUrl too; expose them
    // so the buttons can render the tenant logo when those providers are the
    // tenant's IdP (parity with the Angular 8 login component).
    this.oktaProvider = byType('okta');
    this.microsoftProvider = byType('microsoft');

    const exclusive = descriptors.some((p: SsoProviderDescriptor) => p.exclusive === true);
    this.showOktaButton = !this.hideOtherSsoButtons && (!exclusive || !!this.oktaProvider);
    this.showMicrosoftButton = !this.hideOtherSsoButtons && (!exclusive || !!this.microsoftProvider);
  }

  private cacheKeyForHost(): string | null {
    // Angular Universal pre-renders have no `window`. Return null so
    // every cache entry point becomes a no-op on the server and the
    // SPA hydrates on the client like a cold load.
    if (!isPlatformBrowser(this.platformId)) return null;
    return LoginComponent.SSO_PROVIDERS_CACHE_KEY_PREFIX + window.location.hostname;
  }

  private readCachedSsoProviders(): SsoProviderDescriptor[] | null {
    const key = this.cacheKeyForHost();
    if (!key) return null;
    try {
      const raw = this.localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.descriptors) || typeof parsed.ts !== 'number') {
        return null;
      }
      if (Date.now() - parsed.ts > LoginComponent.SSO_PROVIDERS_CACHE_TTL_MS) {
        return null;
      }
      return parsed.descriptors;
    } catch {
      return null;
    }
  }

  private writeCachedSsoProviders(descriptors: SsoProviderDescriptor[]): void {
    const key = this.cacheKeyForHost();
    if (!key) return;
    try {
      this.localStorage.setItem(key, JSON.stringify({
        descriptors,
        ts: Date.now(),
      }));
    } catch {
      // localStorage may be unavailable (private browsing, quota); the
      // next visit will just rehit the backend like any cold load.
    }
  }

  private clearSsoFallbackTimer(): void {
    if (this.ssoProvidersFallbackTimer !== null) {
      clearTimeout(this.ssoProvidersFallbackTimer);
      this.ssoProvidersFallbackTimer = null;
    }
  }

  onPingSignOn() {
    if (this.isIframe || !this.pingProvider) return;
    this.pingSso.configure(this.pingProvider);
    this.localStorage.setItem('SSOLoginInProgress', 'true');
    this.accessTokenService.showLoginUI = false;
    this.pingSso.signIn().catch((err) => {
      console.error('[PingSso] signIn failed', err);
      this.accessTokenService.showLoginUI = true;
    });
  }

  async onOktaSignOn() {
    if (this.isIframe) return;
    await this.oktaAuth.clearStorage();
    this.localStorage.setItem('oktaLogin', 'true');
    await this.oktaAuth.signInWithRedirect({ originalUri: '/dashboard' });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginDisplay = false;

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  onLoginSubmit() {
    this.submitted = true;

    this.loginFormGroup.markAllAsTouched();
    this.authenticateUser();
  }

  authenticateUser() {

    // if (this.isSSOEnabled === true) {
    //   this.alertService.openAlert('Please login with Microsoft.', 'error');
    //   return;
    // }

    if (this.loginFormGroup.invalid) return;

    const loginRequestData = {
      UserEmail: this.loginFormGroup.value.email,
      password: this.loginFormGroup.value.password,
    }

    this.errorMessage = '';

    this._userDataProviderService.validateUser(loginRequestData).subscribe((response: any) => {
      this.errorMessage = '';

      if (response.status == 'success') {
        // Set Access Token in Local storage
        this.localStorage.setItem('accessToken', response.data);
        this.analytics.setUser(this.accessTokenService.getUserId(), this.accessTokenService.getUserRole());
        this.analytics.trackLogin('password');
        this._userDataProviderService.isShowSidebar.emit(true);
        this.routerService.navigateByUrl('/dashboard');
      } else {
        this.accessTokenService.clearTokens();
        this.analytics.trackLoginFailed('password', 'invalid_credentials');
        this.alertService.openAlert(this.translate.instant('Invalid email or password !'), 'error');
      }
    });
  }


  togglePassword() {
    this.fieldTextType = !this.fieldTextType;
    // pwd.type=pwd.type=='password'?'text':'password'
  }

  getClientDetails() {
    this.SettingsService.getClientDetails().subscribe((res: any) => {
      if (res.status == 'success') {
        this.loginLogo = res.data[0]?.ClientDetails || 'assets/new-icons/Spendkey_logo_dark.svg';
        this.localStorage.setItem('ClientLogo', this.loginLogo);
        const clientNameFromApi = res.data[1]?.ClientDetails;
        if (clientNameFromApi && clientNameFromApi !== 'Spendkey' && clientNameFromApi !== '') {
          this.clientName = clientNameFromApi;
          this.spendkeyFav = true;
        } else {
          this.clientName = 'Spendkey';
          this.spendkeyFav = false;
        }
      } else {
        this.loginLogo = 'assets/new-icons/Spendkey_logo_dark.svg';
      }
      this.spendkeyLoadLogo = true; // load logo
    })
  }

  viewTerms() {
    const dialogRef = this.dialog.open(TermsConditionsComponent, {
      panelClass: 'sk-mat-modal-policy',
      disableClose: false
    });
  }

  viewPolicy() {
    const dialogRef = this.dialog.open(PrivacyPolicyComponent, {
      panelClass: 'sk-mat-modal-policy',
      disableClose: false
    });
  }

  ngOnDestroy(): void {
    this.clearSsoFallbackTimer();
    this._destroying$.next();
    this._destroying$.complete();
  }

}
